/**
 * A Life Story as its own highlightable content scope.
 *
 * Stories live inside a `life-stories` block on a lesson page but are READ on a
 * route of their own (`/courses/:programSlug/:lessonId/story/:storyId`). That
 * split is why they get a scope rather than a slice of the lesson page's word
 * index:
 *
 *   - The page's `globalIndex` runs across the blocks the page RENDERS. Feeding
 *     story words into it would give the Life Stories card a `wordRange`, and a
 *     `wordRange` is what makes a block own an end-of-block note row — the card
 *     would grow rows for marks made on a screen the reader is not looking at,
 *     and a highlight next to the card would take story text as its context.
 *   - Every story in one block would share that index space, so adding a story
 *     to a category, or reordering categories in the builder, would shift every
 *     index after it. Per story, an edit to one story cannot disturb another.
 *
 * A highlight still BELONGS to the lesson: it is saved with the lesson's slug and
 * the page index of the block the story sits in, and `blockTreeHasId` resolves a
 * story anchor back to that page. Only the word numbering is story-local.
 *
 * The API stores `blockId` as an opaque string (`@IsString()`, written to the
 * column verbatim and never checked against lesson content), so an anchor id
 * minted here round-trips with no backend change.
 */

import { fieldRuns } from '@/pages/courses/rich-field';

const ANCHOR_PREFIX = 'story';

// Which part of a story an anchor addresses. Only `body` is emitted today — the
// title, the category eyebrow and the byline render as they always have, through
// RichText, and are not highlightable. `title` and `byline` are reserved so
// making them highlightable later does not need a new id format.
export const STORY_ANCHOR_PARTS = { TITLE: 'title', BYLINE: 'byline', BODY: 'body' };

/**
 * `story:<part>:<storyId>`.
 *
 * The part comes BEFORE the id on purpose: a story id is server-minted and may
 * itself contain a colon, so putting it last means everything after the second
 * separator is the id and parsing cannot be ambiguous. The parts are fixed words
 * with no colon in them.
 */
export function storyAnchorId(part, storyId) {
  return `${ANCHOR_PREFIX}:${part}:${storyId}`;
}

export function isStoryAnchor(blockId) {
  return typeof blockId === 'string' && blockId.startsWith(`${ANCHOR_PREFIX}:`);
}

/** The story id inside an anchor, or null for anything that is not one. */
export function storyIdFromAnchor(blockId) {
  if (!isStoryAnchor(blockId)) return null;
  // Split into exactly three: prefix, part, and the id — which keeps every
  // remaining colon inside the id where it belongs.
  const [, , ...rest] = blockId.split(':');
  const storyId = rest.join(':');
  return storyId || null;
}

/**
 * The `state.highlights` bucket one story's marks live in.
 *
 * Separate from the page's bucket because the two number spaces collide: story
 * indices are story-local and page indices are page-global, so a story mark at
 * local index 42 filed under the page would paint itself over the lesson's word
 * 42. The store keys this map by an opaque string, so a compound key needs no
 * mutation changes.
 */
export function storyScopeKey(pageId, storyId) {
  return `${pageId || '_'}::story::${storyId}`;
}

/** True when this block is one of the two Life Stories block types. */
export function isLifeStoriesBlock(block) {
  return block?.type === 'life-stories' || block?.type === 'life-stories-optional';
}

/**
 * Find a story anywhere in a block tree, along with the `life-stories` block
 * that holds it — a story's own fields (category, author, …) are per-person,
 * but which named resource it belongs to (the block's authored `title`, e.g.
 * "Stories of Breakthroughs from Strongholds") is only visible on the block.
 *
 * Nested children live on the parent note/accordion, so the walk goes through
 * `block.blocks` rather than only the page's top level.
 */
export function findStoryInBlock(block, storyId) {
  if (isLifeStoriesBlock(block)) {
    const story = (block.stories || []).find(s => String(s.id) === String(storyId));
    if (story) return { story, block };
  }
  for (const child of block.blocks || []) {
    const nested = findStoryInBlock(child, storyId);
    if (nested) return nested;
  }
  return null;
}

/**
 * The story with this id, the page it was found on, and the life-stories
 * block it belongs to.
 *
 * The page is what makes a story highlight a LESSON highlight: it supplies the
 * `pageIndex` the highlight is saved with and the bucket its scope key hangs
 * off.
 */
export function findStoryInPages(pages, storyId) {
  const list = Array.isArray(pages) ? pages : [];
  for (let pageIndex = 0; pageIndex < list.length; pageIndex += 1) {
    const page = list[pageIndex];
    for (const block of page?.blocks || []) {
      const found = findStoryInBlock(block, storyId);
      if (found) return { story: found.story, block: found.block, page, pageIndex };
    }
  }
  return null;
}

// The runs a story's body renders from. `bodyInline` is the rich form and `body`
// its plain mirror; a story with neither falls back to the excerpt, which is what
// the card shows and is better than an empty page. Matches _storyId.vue's own
// bodyRuns, which is the text the reader actually sees.
export function storyBodyRuns(story) {
  if (!story) return [];
  const body = fieldRuns(story.bodyInline, story.body, { lists: true });
  if (body.length) return body;
  return fieldRuns(story.excerptInline, story.excerpt, { lists: true });
}

/**
 * The story's body as the one block the lesson renderer tokenises it from.
 *
 * A `paragraph` rather than one block per paragraph: the tokeniser keeps the real
 * whitespace between words when a run is processed with `preserveWhitespace`, and
 * WordRun renders it under `white-space: pre-wrap`. So the body's own blank lines
 * survive as blank lines, exactly as the read-only `pre-line` rendering showed
 * them, and the body keeps a single stable anchor id.
 *
 * Returns [] for a story with no body — the renderer would tokenise nothing and
 * an empty block would still claim a note row.
 */
export function storyBlocks(story) {
  const runs = storyBodyRuns(story);
  if (!story || !runs.length) return [];

  return [
    {
      id: storyAnchorId(STORY_ANCHOR_PARTS.BODY, story.id),
      type: 'paragraph',
      // `type: 'text'` on every run: the paragraph branch tokenises a run it
      // recognises and passes anything else through untouched, and a list keeps
      // its own type so nested bullets still reach processList.
      inline: runs.map(run => (run.type === 'list' ? run : { ...run, type: 'text' })),
    },
  ];
}
